import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../music24/components/primitives/SMLButton';
import { ServiceConnection } from '../../../../redux/api/fymAPI.types';
import { useAccountId } from '../../../../redux/slices/appInfo/hooks';
import { useIsDemoAccount } from '../../../accountSelector/useIsDemoAccount';
import { useSoundCloudConnect } from '../../../music24/soundcloud/useSoundCloudConnect';
import { isSoundCloudConnectFailed } from '../../../music24/soundcloud/soundCloudConnectSession';
import { useConfirmWhileBusy } from '../../../common/views/modals/confirmDialog/useConfirmWhileBusy';
import { EarlyAccessServiceMark } from '../earlyAccess/EarlyAccessServiceMark';
import { soundCloudCardState } from './soundCloudCardState';
import { useSoundCloudCardTexts } from './useSoundCloudCardTexts';
import { SoundCloudCardProfile } from './SoundCloudCardProfile';
import { SoundCloudDisconnectModal } from './SoundCloudDisconnectModal';
import { SoundCloudInsightsNote } from './SoundCloudInsightsNote';
import { soundCloudCardMessages as m } from './soundCloudCardMessages';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- shared stylesheets; each part uses its own slice of them */
import styles from '../Catalog.module.scss';
import own from './SoundCloudCard.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SoundCloudConnectionCardProps {
  connection: ServiceConnection;
  onSync: (integrationType: string) => void;
  onDisconnect: (integrationType: string) => void;
  isBusy: boolean;
  now?: number;
}

/**
 * The SoundCloud card of Catalog → Sources (design a1-a4; spec 2026-10-15 D9: every user, no flag).
 * Connect leaves for the sign-in that MusicAPI runs; the return page finishes it (design b). Every
 * user may upload (owner decision 2026-10-18: no admin switch for the SoundCloud upload).
 */
const SoundCloudConnectionCardComponent: React.FC<SoundCloudConnectionCardProps> = ({
  connection,
  onSync,
  onDisconnect,
  isBusy,
  now,
}) => {
  const intl = useIntl();
  // The demo artist: the card with no buttons (design a2).
  const readOnly = useIsDemoAccount();
  const accountId = useAccountId() ?? 0;
  const soundCloud = useSoundCloudConnect({ accountId });
  const [connectFailed] = useState(isSoundCloudConnectFailed);
  const state = soundCloudCardState(connection, { isConnecting: soundCloud.isConnecting, connectFailed });
  const texts = useSoundCloudCardTexts({ connection, state, now });
  const { integrationType } = connection;
  const connect = useCallback(() => {
    soundCloud.connect().catch(console.error);
  }, [soundCloud]);
  const sync = useCallback(() => onSync(integrationType), [integrationType, onSync]);
  const disconnect = useCallback(() => onDisconnect(integrationType), [integrationType, onDisconnect]);
  const disconnectDialog = useConfirmWhileBusy({ isBusy, onConfirm: disconnect });
  const isOn = state === 'connected' || state === 'firstRead';
  const hasProfile = isOn && connection.artist !== null;
  const dotClass = useMemo(
    () =>
      clsx(
        styles.statusDot,
        state === 'connected' && styles.statusDotOn,
        state === 'connecting' && styles.statusDotPending
      ),
    [state]
  );

  return (
    <div
      className={clsx(styles.connectionCard, !isOn && styles.connectionCardOff)}
      data-testid="soundcloud-card"
      data-state={state}
    >
      <EarlyAccessServiceMark integrationType={integrationType} />
      <div className={styles.connectionBody}>
        <div className={styles.connectionName}>
          <span className={dotClass} />
          {intl.formatMessage(m.name)}
        </div>
        <div className={styles.connectionHandle}>{texts.handle}</div>
        <div className={styles.connectionNote}>{withBrandWord(texts.note)}</div>
        {hasProfile && <SoundCloudCardProfile connection={connection} />}
        {texts.line !== null && (
          <div
            className={clsx(own.line, texts.lineTone === 'ok' && own.lineOk, texts.lineTone === 'bad' && own.lineBad)}
          >
            {texts.isWorking && <Spinner size="sm" />}
            {withBrandWord(texts.line)}
          </div>
        )}
        <SoundCloudInsightsNote connection={connection} cardState={state} />
        {state === 'failed' && (
          <div className={own.alert} role="alert">
            <FontAwesomeIcon icon={faCircleExclamation} />
            <span>{intl.formatMessage(m.failed)}</span>
          </div>
        )}
        {!readOnly && (
          <div className={styles.connectionActions}>
            {state === 'connected' && (
              <span className={clsx(styles.pill, styles.pillVerified)}>{intl.formatMessage(m.verified)}</span>
            )}
            {state === 'reconnect' && (
              <span className={clsx(styles.pill, own.pillBad)}>{intl.formatMessage(m.pillSignedOut)}</span>
            )}
            {(state === 'notConnected' || state === 'failed' || state === 'reconnect') && (
              <SMLButton variant="primary" onClick={connect} className={styles.smallButton} disabled={isBusy}>
                {intl.formatMessage(
                  state === 'notConnected' ? m.connect : state === 'failed' ? m.tryAgain : m.reconnect
                )}
              </SMLButton>
            )}
            {state === 'connecting' && (
              <SMLButton onClick={soundCloud.reset} className={styles.smallButton}>
                {intl.formatMessage(m.cancel)}
              </SMLButton>
            )}
            {state === 'connected' && (
              <SMLButton onClick={sync} className={styles.smallButton} disabled={isBusy}>
                {intl.formatMessage(m.syncNow)}
              </SMLButton>
            )}
            {(state === 'connected' || state === 'firstRead' || state === 'reconnect') && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={disconnectDialog.open}
                disabled={isBusy}
              >
                {intl.formatMessage(m.disconnect)}
              </button>
            )}
          </div>
        )}
      </div>
      <SoundCloudDisconnectModal
        accountId={accountId}
        connection={connection}
        isOpen={disconnectDialog.isOpen}
        isBusy={disconnectDialog.isRunning}
        onCancel={disconnectDialog.cancel}
        onConfirm={disconnectDialog.confirm}
      />
    </div>
  );
};

export const SoundCloudConnectionCard = TypedMemo(SoundCloudConnectionCardComponent);
